import { useMediaQuery } from '@material-ui/core'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import moment from 'moment'
import * as React from 'react'
import { animated, useSpring } from 'react-spring'
import styled from 'styled-components'
import { EnrichedReceipt } from '.'
import SkeletonItem from './SkeletonItem'
import { useRouter } from 'next/router'
import { useAtom } from 'jotai'
import { rwSolPriceAtom } from '../../../atoms/WalletName'

interface ActivityItemProps {
  receipt?: EnrichedReceipt
  hideTimestamp?: boolean
}

const ActivityItem = ({ receipt, hideTimestamp }: ActivityItemProps): JSX.Element => {
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [vidHover, setVidHover] = React.useState(false)
  const [profileHover, setProfileHover] = React.useState(false)
  const [solPrice, setPrice] = useAtom(rwSolPriceAtom)
  const [secondProfileHover, setSecondProfileHover] = React.useState(false)
  const { buyer, seller, receiptType } = receipt || {}
  const createdAt = receipt?.createdAt ? new Date(receipt.createdAt) : new Date()
  const price = receipt?.price
    ? String(Math.round((100 * receipt.price) / LAMPORTS_PER_SOL) / 100)
    : '0'
  const router = useRouter()

  const vidHoverState = useSpring({
    cursor: 'pointer',
    width: '110px',
    transform: vidHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const titleHoverState = useSpring({
    cursor: 'pointer',
    textDecoration: vidHover ? 'underline' : 'none',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const firstHoverState = useSpring({
    cursor: 'pointer',
    textDecoration: profileHover ? 'underline' : 'none',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const secondHoverState = useSpring({
    cursor: 'pointer',
    textDecoration: secondProfileHover ? 'underline' : 'none',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const handleOpenProfile = (profile: string): void => {
    router.push(`/${profile}`)
  }

  const handleOpenVideo = (video: string): void => {
    router.push(`/watch/${video}`)
  }

  const getSolPrice = async (): Promise<void> => {
    const resp = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?vs_currencies=usd&ids=solana'
    )
    const json = await resp.json()
    setPrice(json['solana']['usd'] as number)
  }

  React.useEffect(() => {
    if (solPrice === 0) {
      getSolPrice()
    }
  }, [])

  if (!receipt) {
    return <SkeletonItem />
  }

  return (
    <OuterDiv>
      <InnerDiv>
        <StartDiv>
          {!isMobile && buyer?.address && (
            <animated.div
              onMouseEnter={() => setVidHover(true)}
              onMouseLeave={() => setVidHover(false)}
              style={vidHoverState}
              onClick={() => handleOpenVideo(receipt.video.address)}
            >
              <VideoThumbnailContainer>
                <VideoThumbnail
                  poster={`https://glassimg.imgix.net/${receipt.video.thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=400&h=225`}
                />
              </VideoThumbnailContainer>
            </animated.div>
          )}
          <EventDiv>
            <TitleDiv>
              <Title
                onMouseEnter={() => setVidHover(true)}
                onMouseLeave={() => setVidHover(false)}
                style={titleHoverState}
                neutral
                hasTitle
                onClick={() => handleOpenVideo(receipt.video.address)}
              >
                {receipt.video.title}
              </Title>
            </TitleDiv>
            <TitleDiv>
              {buyer?.displayName && (
                <Subtitle
                  onMouseEnter={() => setProfileHover(true)}
                  onMouseLeave={() => setProfileHover(false)}
                  style={firstHoverState}
                  onClick={() => handleOpenProfile(buyer.address)}
                >
                  {buyer.displayName}
                </Subtitle>
              )}
              <Subtitle>
                {(receiptType === 'primary' ? ' minted' : ' bought') +
                  (isMobile
                    ? ''
                    : ` ${
                        receipt.tokenSize > 1 && receipt.tokenSize ? receipt.tokenSize : 'an'
                      } edition${receipt.tokenSize > 1 && receipt.tokenSize ? 's' : ''}`)}
              </Subtitle>
              {seller?.displayName && <Subtitle>{` from `}</Subtitle>}
              {seller?.displayName && (
                <Subtitle
                  onMouseEnter={() => setSecondProfileHover(true)}
                  onMouseLeave={() => setSecondProfileHover(false)}
                  style={secondHoverState}
                  onClick={() => handleOpenProfile(seller.address)}
                >
                  {seller.displayName}
                </Subtitle>
              )}
              <Subtitle>.</Subtitle>
            </TitleDiv>
          </EventDiv>
        </StartDiv>
        <EndDiv>
          <PriceDiv>
            <Title neutral={false}>{price + ' SOL'}</Title>
            <Subtitle>
              {hideTimestamp
                ? '$' + (Number(price) * solPrice).toFixed(2)
                : moment(createdAt) > moment(new Date()).add(-1, 'day') || isMobile
                ? moment(createdAt).fromNow()
                : moment(createdAt).format('h:mm a') + ', ' + moment(createdAt).format('M/D/YY')}
            </Subtitle>
          </PriceDiv>
        </EndDiv>
      </InnerDiv>
      <Divider />
    </OuterDiv>
  )
}

export default ActivityItem

const OuterDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 15px;
`

const InnerDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const VideoThumbnailContainer = styled(animated.div)`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 7.5px;

  @media (min-width: 62em) {
    border-radius: 5px;
  }
`

const VideoThumbnail = styled(animated.div)<{ poster: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  background-color: black;
  cursor: pointer;
  background-image: url(${(props) => props.poster});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  @media (min-width: 62em) {
    border-radius: 5px;
  }
`

const EventDiv = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 43em) {
    margin-left: 15px;
  }
`

const EndDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const PriceDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const TitleDiv = styled.div`
  display: flex;
  flex-direction: row;
`

const Title = styled(animated.span)<{ neutral: boolean; hasTitle?: boolean }>`
  color: ${(props) => (props.neutral ? 'white' : 'rgb(48, 209, 88)')};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  white-space: pre;
  max-width: ${(props) => (props.hasTitle ? '300px' : '')};
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 43em) {
    font-size: 17px;
    max-width: ${(props) => (props.hasTitle ? '400px' : '')};
  }
`

const Subtitle = styled(animated.span)`
  color: #cdcdcd;
  font-size: 14px;
  font-weight: 400;
  white-space: pre;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const StartDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Divider = styled.div`
  width: 100%;
  background: #323232;
  height: 1px;
  margin-top: 15px;
`
